import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { can } from "@/lib/permissions";
import { PageHeader, InfoNote } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATE_CLS = {
  "Not Started": "text-slate-400", "In Progress": "text-amber-600",
  Complete: "text-emerald-600", Blocked: "text-rose-600", "To Be Confirmed": "text-slate-500",
};

export default function PilotReadiness() {
  const { roleCode } = useApp();
  const [data, setData] = useState(null);
  const load = useCallback(() => api.pilotReadiness().then(setData).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-sm text-slate-500">Loading readiness gate…</div>;

  const isAdmin = can.admin(roleCode);
  const pct = data.total ? Math.round((data.complete / data.total) * 100) : 0;
  const setItem = async (key, status) => {
    try { await api.setReadinessItem({ item_key: key, status, role_code: roleCode }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Only System Administrator may update readiness."); }
  };

  return (
    <div>
      <PageHeader testid="pilot-readiness-header" title="Live Pilot Readiness Gate"
        subtitle="A non-operational checklist. RequiSure will not imply live readiness until every requirement is complete." />
      <Card className={`mb-4 flex flex-wrap items-center justify-between gap-4 border-l-4 p-4 ${data.overall.startsWith("READY") ? "border-l-emerald-500" : "border-l-rose-500"}`} data-testid="readiness-overall">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Overall</div>
          <div className={`font-display text-xl font-bold ${data.overall.startsWith("READY") ? "text-emerald-700" : "text-rose-700"}`}>{data.overall}</div>
        </div>
        <div className="min-w-[240px] flex-1">
          <div className="mb-1 flex justify-between text-xs text-slate-500"><span>Requirements complete</span><span>{data.complete} / {data.total}</span></div>
          <Progress value={pct} className="h-2" />
        </div>
      </Card>
      {!isAdmin && <InfoNote className="mb-4">Only the System Administrator persona can update readiness items. Switch role to “System Administrator” to edit.</InfoNote>}

      <div className="grid gap-3 md:grid-cols-2">
        {data.categories.map((c) => (
          <Card key={c.category} className="p-4" data-testid={`readiness-cat-${c.category.toLowerCase()}`}>
            <div className="mb-2 font-display text-sm font-bold text-slate-900">{c.category}</div>
            <div className="space-y-1.5">
              {c.items.map((it) => (
                <div key={it.key} className="flex items-center justify-between gap-2 border-b border-slate-100 py-1">
                  <span className="text-xs text-slate-700">{it.label}</span>
                  {isAdmin ? (
                    <Select value={it.status} onValueChange={(v) => setItem(it.key, v)}>
                      <SelectTrigger className="h-7 w-36 text-[11px]" data-testid={`readiness-item-${it.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{data.states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <span className={`text-[11px] font-semibold ${STATE_CLS[it.status] || "text-slate-400"}`}>{it.status}</span>
                  )}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
